import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Map as MapIcon,
  Upload as UploadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getMaps, createMap, uploadMapImage, updateMap, deleteMap } from '../../services/mapService';
import { useNavigate } from 'react-router-dom';

const MapsPage = () => {
  const { firestoreUser, isUltraAuth } = useAuth();
  const navigate = useNavigate();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMaps();
  }, []);

  const loadMaps = async () => {
    try {
      const mapsData = await getMaps();
      setMaps(mapsData);
    } catch (error) {
      console.error('Error loading maps:', error);
      setError('Failed to load maps');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPG, PNG, GIF, WebP, or BMP)');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCreateMap = async (e) => {
    e.preventDefault();
    if (!selectedFile || !formData.title.trim()) {
      setError('Please provide a title and select an image');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Create map document first
      const mapId = await createMap({
        title: formData.title.trim(),
        description: formData.description.trim(),
        createdBy: firestoreUser.uid,
        createdByName: firestoreUser.displayName || 'Unknown User'
      });

      // Upload image
      const imageUrl = await uploadMapImage(selectedFile, mapId);
      
      // Update map with image URL
      await updateMap(mapId, { imageUrl });

      // Reload maps
      await loadMaps();
      
      // Reset form
      resetCreateDialog();
    } catch (error) {
      console.error('Error creating map:', error);
      setError('Failed to create map: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const resetCreateDialog = () => {
    setCreateDialog(false);
    setFormData({ title: '', description: '' });
    setSelectedFile(null);
    setImagePreview(null);
    setError('');
  };

  const handleDeleteMap = async (mapId, mapTitle) => {
    if (window.confirm(`Are you sure you want to delete "${mapTitle}" and all its pins? This cannot be undone.`)) {
      try {
        await deleteMap(mapId);
        await loadMaps();
      } catch (error) {
        console.error('Error deleting map:', error);
        setError('Failed to delete map');
      }
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
        <Typography sx={{ ml: 2, color: '#F5F5F5' }}>Loading maps...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#2c2c2c' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ 
          color: '#d98d2a', 
          fontFamily: '"Georgia", serif',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          🗺️ Campaign Maps
        </Typography>
        {isUltraAuth && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            sx={{ 
              backgroundColor: '#d98d2a', 
              '&:hover': { backgroundColor: '#c87d26' },
              fontWeight: 'bold'
            }}
          >
            Add New Map
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {maps.length === 0 ? (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: '#3c3c3c',
          border: '1px solid #555'
        }}>
          <MapIcon sx={{ fontSize: '4rem', color: '#666', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#F5F5F5', mb: 2 }}>
            No maps available yet
          </Typography>
          <Typography sx={{ color: '#A9A9A9', mb: 3 }}>
            Upload your first campaign map to start pinning locations for your adventures!
          </Typography>
          {isUltraAuth && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
              sx={{ backgroundColor: '#d98d2a', '&:hover': { backgroundColor: '#c87d26' } }}
            >
              Create Your First Map
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {maps.map((map) => (
            <Grid item xs={12} sm={6} md={4} key={map.id}>
              <Card sx={{ 
                backgroundColor: '#3c3c3c', 
                border: '1px solid #555',
                '&:hover': { 
                  boxShadow: '0 4px 20px rgba(217, 141, 42, 0.3)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={map.imageUrl}
                  alt={map.title}
                  sx={{ 
                    cursor: 'pointer',
                    objectFit: 'cover'
                  }}
                  onClick={() => navigate(`/maps/${map.id}`)}
                />
                <CardContent>
                  <Typography variant="h6" sx={{ 
                    color: '#d98d2a', 
                    fontWeight: 'bold',
                    mb: 1,
                    fontFamily: '"Georgia", serif'
                  }}>
                    {map.title}
                  </Typography>
                  {map.description && (
                    <Typography variant="body2" sx={{ 
                      color: '#A9A9A9', 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {map.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip 
                      label={`By ${map.createdByName || 'Unknown'}`}
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(217, 141, 42, 0.2)',
                        color: '#d98d2a',
                        fontSize: '0.75rem'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      {formatDate(map.createdAt)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => navigate(`/maps/${map.id}`)}
                    sx={{ color: '#d98d2a', fontWeight: 'medium' }}
                  >
                    View Map
                  </Button>
                  {isUltraAuth && map.createdBy === firestoreUser.uid && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteMap(map.id, map.title)}
                      sx={{ color: '#ff6b6b' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Map Dialog */}
      <Dialog 
        open={createDialog} 
        onClose={resetCreateDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            backgroundColor: '#3c3c3c',
            color: '#F5F5F5',
            border: '1px solid #555'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: '#d98d2a',
          fontFamily: '"Georgia", serif'
        }}>
          <Typography variant="h6">Create New Campaign Map</Typography>
          <IconButton onClick={resetCreateDialog} sx={{ color: '#F5F5F5' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreateMap} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Map Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
              required
              placeholder="e.g., 'The Forgotten Realms', 'Dungeon Level 1'"
              InputLabelProps={{ style: { color: '#d98d2a' } }}
              inputProps={{ style: { color: '#F5F5F5' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d98d2a' },
                  '&:hover fieldset': { borderColor: '#c87d26' },
                  '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              placeholder="Describe this map, its purpose, or any important details..."
              InputLabelProps={{ style: { color: '#d98d2a' } }}
              inputProps={{ style: { color: '#F5F5F5' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d98d2a' },
                  '&:hover fieldset': { borderColor: '#c87d26' },
                  '&.Mui-focused fieldset': { borderColor: '#d98d2a' }
                }
              }}
            />

            <Paper sx={{ 
              p: 3, 
              mt: 3, 
              backgroundColor: '#2c2c2c', 
              border: '2px dashed #d98d2a',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ color: '#d98d2a', mb: 2 }}>
                Upload Map Image
              </Typography>
              
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                sx={{
                  color: '#d98d2a',
                  borderColor: '#d98d2a',
                  mb: 2,
                  '&:hover': { 
                    borderColor: '#c87d26', 
                    backgroundColor: 'rgba(217, 141, 42, 0.1)' 
                  }
                }}
              >
                Choose Image File
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
              
              <Typography variant="body2" sx={{ color: '#A9A9A9', mb: 2 }}>
                Supported formats: JPG, PNG, GIF, WebP, BMP (Max 10MB)
              </Typography>
              
              {imagePreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '300px', 
                      borderRadius: '8px',
                      border: '2px solid #d98d2a'
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#A9A9A9', mt: 1 }}>
                    Selected: {selectedFile?.name}
                  </Typography>
                </Box>
              )}
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={resetCreateDialog} disabled={uploading}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={!selectedFile || !formData.title.trim() || uploading}
                sx={{ 
                  backgroundColor: '#d98d2a', 
                  '&:hover': { backgroundColor: '#c87d26' } 
                }}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Creating...
                  </>
                ) : (
                  'Create Map'
                )}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MapsPage;